import { useCallback, useEffect, useRef, useState } from "react";
import { loadRouteCache, saveRouteCache } from "./routeCacheStorage";
import type { RouteCache, UseRouteCache } from "./types";

export const useRouteCache: UseRouteCache = (storageKey) => {
  const [cache, setCache] = useState<RouteCache>(() => loadRouteCache(storageKey));
  const failedRef = useRef(new Set<string>());
  const loadedKeyRef = useRef(storageKey);

  const addAnswers = useCallback(
    (answers: Parameters<ReturnType<UseRouteCache>["addAnswers"]>[0]) => {
      const fresh = answers.filter(
        (entry): entry is [string, NonNullable<(typeof entry)[1]>] => entry[1] !== null,
      );
      answers.forEach(([key, answer]) => {
        if (!answer) failedRef.current.add(key);
      });
      if (!fresh.length) return;
      setCache((current) => ({ ...current, ...Object.fromEntries(fresh) }));
    },
    [],
  );

  const isFailed = useCallback((key: string) => failedRef.current.has(key), []);

  useEffect(() => {
    if (loadedKeyRef.current === storageKey) return;
    loadedKeyRef.current = storageKey;
    failedRef.current = new Set();
    setCache(loadRouteCache(storageKey));
  }, [storageKey]);

  useEffect(() => {
    if (loadedKeyRef.current !== storageKey) return;
    saveRouteCache(storageKey, cache);
  }, [cache, storageKey]);

  return { cache, addAnswers, isFailed };
};
